// VoiceBooking.jsx — Trustidrive AI Voice Ride Booking Screen
import React, { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import CustomerMasterLayout from "../../../layouts/Customer/CustomerMasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import "../../../assets/css/Customer/voicebooking/VoiceBooking.css";

/* ── Icons ── */
const IconBack = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const IconMic = () => (
  <svg
    width="32"
    height="32"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <rect
      x="9"
      y="2"
      width="6"
      height="12"
      rx="3"
      fill="currentColor"
      stroke="none"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 10a7 7 0 0014 0" />
    <line x1="12" y1="17" x2="12" y2="21" strokeLinecap="round" />
    <line x1="9" y1="21" x2="15" y2="21" strokeLinecap="round" />
  </svg>
);

const IconMicOff = () => (
  <svg
    width="32"
    height="32"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6M3 3l18 18"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 16.95A7 7 0 015 10"
    />
    <line x1="12" y1="19" x2="12" y2="23" strokeLinecap="round" />
    <line x1="9" y1="23" x2="15" y2="23" strokeLinecap="round" />
  </svg>
);

const IconInfo = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.2}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="8" strokeLinecap="round" strokeWidth={3} />
    <line x1="12" y1="12" x2="12" y2="16" strokeLinecap="round" />
  </svg>
);

const IconLightbulb = () => (
  <svg
    width="18"
    height="18"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
    />
  </svg>
);

const IconCheckCircle = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/* ── Steps ── */
const STEPS = [
  { title: "Say Your Pickup & Drop Location" },
  { title: "Confirm Your Ride Type" },
  { title: "Booking Will Be Confirmed" },
];

/* ── Keyword maps for parsing the spoken transcript ── */
const VEHICLE_KEYWORDS = {
  "seven seater": "seater7",
  "7 seater": "seater7",
  van: "seater7",
  "four seater": "seater4",
  "4 seater": "seater4",
  sedan: "seater4",
  car: "seater4",
};

const PAYMENT_KEYWORDS = {
  cash: "cash",
  coin: "coin",
  coins: "coin",
  later: "baadmay",
  installment: "baadmay",
  installments: "baadmay",
  baadmay: "baadmay",
};

const capitalizeWords = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Vehicle pricing (same values as RideSelection.jsx) ── */
const RIDE_PRICES = {
  seater4: 2500,
  seater7: 3200,
};
const getFareAmount = (type, passengers) =>
  (RIDE_PRICES[type] || RIDE_PRICES.seater4) * passengers;

const VoiceBooking = () => {
  const navigate = useNavigate();
  const { baseUrl, selectedRideType, setSelectedRideforFindingDriver } = useContext(AppContext);
  const authToken = localStorage.getItem("authToken");

  const [pickuplocation, setpickuplocation] = useState("");
  const [dropofflocation, setdropofflocation] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [bookingType, setBookingType] = useState("");
  const [vehicleType, setvehicleType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [offeredFare, setofferedFare] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [driverType, setDriverType] = useState("male");
  const [femaleAvailable] = useState(false);
  const [customerFareBid, setCustomerFareBid] = useState(0);
  const [fareEdited, setFareEdited] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);

  // ── Voice States ──
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isBooked, setIsBooked] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");

  // ── Same helper variables as RideSelection ──
  const today = new Date().toISOString().split("T")[0];
  const currentTime = new Date().toTimeString().slice(0, 5);

  // Fare: user's manual bid wins if edited, otherwise auto-calc from
  // parsed vehicle type (falls back to seater4) × passengers — same
  // pricing table RideSelection.jsx uses.
  const finalFare = fareEdited
    ? customerFareBid
    : getFareAmount(vehicleType || "seater4", passengers);

  // ── Parse the spoken transcript into structured booking fields ──
  const parseVoiceCommand = (text) => {
    if (!text) return;
    const lower = text.toLowerCase();

    // 1) Pickup & Drop-off — pattern: "from X to Y"
    const fromToMatch = lower.match(
      /from\s+(.+?)\s+to\s+(.+?)(?:\s+by|\s+using|\s+with|\s+in|\s+pay(?:ing)?|\s+via|$)/i
    );
    if (fromToMatch) {
      setpickuplocation(capitalizeWords(fromToMatch[1].trim()));
      setdropofflocation(capitalizeWords(fromToMatch[2].trim()));
    } else {
      // Fallback: "pickup ..." and "drop ..." spoken separately
      const pickupMatch = lower.match(
        /pick\s?up\s+(?:is\s+|at\s+|from\s+)?([a-z0-9\s,]+?)(?=\s+drop|\s+to\s|$)/i
      );
      const dropMatch = lower.match(
        /drop\s?(?:off)?\s+(?:is\s+|at\s+|to\s+)?([a-z0-9\s,]+?)(?=\s+by|\s+using|\s+with|$)/i
      );
      if (pickupMatch) setpickuplocation(capitalizeWords(pickupMatch[1].trim()));
      if (dropMatch) setdropofflocation(capitalizeWords(dropMatch[1].trim()));
    }

    // 2) Vehicle type
    const vehicleKey = Object.keys(VEHICLE_KEYWORDS).find((k) => lower.includes(k));
    if (vehicleKey) {
      setvehicleType(VEHICLE_KEYWORDS[vehicleKey]);
      setBookingType(VEHICLE_KEYWORDS[vehicleKey]);
    }

    // 3) Payment method
    const paymentKey = Object.keys(PAYMENT_KEYWORDS).find((k) => lower.includes(k));
    if (paymentKey) setPaymentMethod(PAYMENT_KEYWORDS[paymentKey]);

    // 4) Fare — "Rs 500" or "500 rupees" (only if user actually said an amount)
    const fareMatch = lower.match(/(?:rs\.?\s?|rupees?\s?)(\d+)|(\d+)\s?(?:rs\.?|rupees?)/i);
    if (fareMatch) {
      const amount = Number(fareMatch[1] || fareMatch[2]);
      setCustomerFareBid(amount);
      setFareEdited(true);
    }

    // 5) Passengers — "2 passengers"
    const passengerMatch = lower.match(/(\d+)\s?(?:passenger|passengers|people|person)/i);
    if (passengerMatch) setPassengers(Number(passengerMatch[1]));
  };

  // ── Toggle mic ──
  const handleMicToggle = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(
        "Your browser does not support voice recognition. Please use Chrome.",
      );
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => {
      const text = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join("");
      setTranscript(text);
      finalTranscriptRef.current = text;
    };
    recognition.onend = () => {
      setIsListening(false);
      parseVoiceCommand(finalTranscriptRef.current);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.start();
    recognitionRef.current = recognition;
  };

  // ── Cancel ──
  const handleCancel = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
    setTranscript("");
    finalTranscriptRef.current = "";
    setpickuplocation("");
    setdropofflocation("");
    setvehicleType("");
    setBookingType("");
    setPaymentMethod("");
    setCustomerFareBid(0);
    setFareEdited(false);
    setPassengers(1);
  };

  // ── handleBookRide ──
  const handleBookRide = () => {
    if (!transcript) {
      alert("Please speak first!");
      return;
    }
    if (!pickuplocation || !dropofflocation) {
      alert(
        'Pickup ya drop-off samajh nahi aaya. Is tarah bolen: "Book a car from Gulshan to Clifton, pay by cash"'
      );
      return;
    }
    if (paymentMethod === "baadmay") {
      setShowInstallmentModal(true);
      return;
    }
    submitRide();
  };

  // ── submitRide ──
  const submitRide = (planId = null) => {
    setIsBooking(true);
    const finalVehicleType = vehicleType || "seater4";
    setofferedFare(finalFare);

    const formData = new FormData();
    formData.append("pickuplocation", pickuplocation);
    formData.append("dropofflocation", dropofflocation);
    formData.append("bookingDate", bookingDate || today);
    formData.append("bookingTime", bookingTime || currentTime);
    formData.append("passengers", passengers);
    formData.append("bookingType", "voice");
    formData.append("paymentmethod", paymentMethod || "cash");
    formData.append("vehicleType", finalVehicleType);
    formData.append(
      "driverType",
      driverType === "female" && !femaleAvailable ? "top_rated" : driverType,
    );
    formData.append("offeredFare", finalFare);
    formData.append("voiceTranscript", transcript);
    formData.append("status", "request");
    if (planId) formData.append("installmentPlan", planId);

    console.log(Object.fromEntries(formData));

    axios
      .post(`${baseUrl}/customer/ride/createRide`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log("FULL RESPONSE:", response.data);
        setIsBooking(false);
        setIsBooked(true);
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          setSelectedRideforFindingDriver(response.data.ride);
          navigate("/customer-finding-driver");
        }, 3000);
      })
      .catch((error) => {
        console.log(error);
        setIsBooking(false);
        alert("Network Error! Please try again.");
      });
  };

  // ── Retry ──
  const handleRetry = () => {
    setIsBooked(false);
    setTranscript("");
    finalTranscriptRef.current = "";
    setpickuplocation("");
    setdropofflocation("");
    setvehicleType("");
    setBookingType("");
    setPaymentMethod("");
    setCustomerFareBid(0);
    setFareEdited(false);
    setPassengers(1);
  };

  return (
    <div className="vb-page">
      <CustomerMasterLayout screenName={"Voice Booking"}>
        <Button
          variant="link"
          className="vb-header__back p-0 border-0"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <IconBack />
        </Button>

        {/* ── Scrollable Content ── */}
        <div className="vb-content">
          <Container fluid className="px-0">
            {/* Instructions Card */}
            <Card className="vb-instructions border-0 mb-3">
              <Card.Body className="p-3">
                <div className="vb-instructions__heading d-flex align-items-center gap-2 mb-3">
                  <IconInfo /> How to Book a Ride
                </div>
                {STEPS.map((step, i) => (
                  <Row key={i} className="vb-step g-0 mb-3 align-items-start">
                    <Col xs="auto">
                      <div className="vb-step__number me-3">{i + 1}</div>
                    </Col>
                    <Col>
                      <p className="vb-step__title mb-1">{step.title}</p>
                      <p className="vb-step__desc mb-0">{step.desc}</p>
                    </Col>
                  </Row>
                ))}
              </Card.Body>
            </Card>

            {/* Transcript Box */}
            <Card className="vb-transcript border-0 mb-3">
              <Card.Body className="p-3">
                <p className="vb-transcript__label mb-2">Your Voice</p>
                <p
                  className={`vb-transcript__text mb-0 ${transcript ? "" : "vb-placeholder"}`}
                >
                  {transcript || "Your speech will appear here..."}
                </p>
              </Card.Body>
            </Card>

            {/* Parsed Details Preview — shown once we have a transcript */}
            {transcript && !isListening && (
              <Card className="vb-transcript border-0 mb-3">
                <Card.Body className="p-3">
                  <p className="vb-transcript__label mb-2">We Understood</p>
                  <Row className="g-2">
                    <Col xs={6}>
                      <small className="text-muted d-block">Pickup</small>
                      <span>{pickuplocation || "—"}</span>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">Drop-off</small>
                      <span>{dropofflocation || "—"}</span>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">Vehicle</small>
                      <span>{vehicleType ? (vehicleType === "seater7" ? "7 Seater" : "4 Seater") : "4 Seater (default)"}</span>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">Payment</small>
                      <span>{paymentMethod || "Cash (default)"}</span>
                    </Col>
                    <Col xs={12}>
                      <small className="text-muted d-block">Estimated Fare</small>
                      <strong>PKR {Number(finalFare).toLocaleString()}</strong>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Pro Tip */}
            <div className="vb-tips d-flex align-items-start gap-2 p-3 rounded-3 mb-2">
              <span className="vb-tips__icon flex-shrink-0 mt-1">
                <IconLightbulb />
              </span>
            </div>
          </Container>
        </div>

        {/* ── Fixed Bottom Bar ── */}
        <div className="vb-bottom-bar">
          <Container fluid className="px-0">
            {isBooked ? (
              <div className="vb-booked-wrap d-flex flex-column align-items-center gap-2">
                <div className="vb-booked-icon">✓</div>
                <p className="vb-booked-title mb-0">
                  Ride Booked Successfully!
                </p>
                <p className="vb-booked-sub mb-1">
                  Looking for nearby drivers...
                </p>
                <Button
                  variant="outline-success"
                  className="vb-retry-btn rounded-pill px-4"
                  onClick={handleRetry}
                >
                  Book Another Ride
                </Button>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center gap-2 w-100">
                {/* Mic Button */}
                <Button
                  className={`vb-mic-btn border-0 ${isListening ? "vb-mic-btn--active" : ""}`}
                  onClick={handleMicToggle}
                  aria-label={
                    isListening ? "Stop listening" : "Start voice booking"
                  }
                >
                  {isListening ? <IconMicOff /> : <IconMic />}
                </Button>

                <span className="vb-mic-btn__label">
                  {isListening ? "Tap again to stop" : "Press mic and speak"}
                </span>

                {/* Book Ride Button */}
                {transcript && !isListening && (
                  <Button
                    className={`vb-book-btn border-0 w-100 rounded-pill d-flex align-items-center justify-content-center gap-2 ${isBooking ? "vb-book-btn--loading" : ""}`}
                    onClick={handleBookRide}
                    disabled={isBooking}
                  >
                    {isBooking ? (
                      <span className="vb-book-btn__spinner" />
                    ) : (
                      <IconCheckCircle />
                    )}
                    {isBooking ? "Booking..." : "Book Ride"}
                  </Button>
                )}

                {/* Cancel / Clear button */}
                {(isListening || transcript) && (
                  <Button
                    variant="outline-secondary"
                    className="vb-cancel-btn rounded-pill px-4"
                    onClick={handleCancel}
                  >
                    {transcript && !isListening ? "Clear & Retry" : "Cancel"}
                  </Button>
                )}
              </div>
            )}
          </Container>
        </div>
      </CustomerMasterLayout>
    </div>
  );
};

export default VoiceBooking;